import { useEffect, useState } from "react";
import { formatDuration, getTrackedSeconds } from '@/utils/taskTime';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  Edit, 
  Trash2, 
  CheckCircle, 
  Clock, 
  User, 
  Calendar, 
  DollarSign,
  AlertTriangle,
  Timer,
  FileText,
  MapPin,
  Tag,
  Users,
  Eye,
  Play,
  Pause,
  Square,
  Star
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useDrag } from 'react-dnd';

const ItemTypes = {
  TASK: 'task',
};

interface EnhancedTask {
  id: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  due_date?: string;
  assigned_to?: string;
  created_at: string;
  case_id?: string;
  task_type?: string;
  category?: string;
  estimated_hours?: number;
  actual_hours?: number;
  hourly_rate?: number;
  is_billable?: boolean;
  completion_percentage?: number;
  recurring_type?: string;
  tags?: string[];
  dependencies?: string[];
  milestone?: boolean;
  attachments?: string[];
  notes?: string;
  reminder_date?: string;
  color_tag?: string;
  is_private?: boolean;
  deadline_critical?: boolean;
  auto_time_tracking?: boolean;
  time_entries?: any;
  location?: string;
  checklist?: any;
  approval_required?: boolean;
  approved_by?: string;
  approved_at?: string;
  court_deadline?: boolean;
  statute_deadline?: boolean;
  cases?: {
    title: string;
    case_number?: string;
  };
}

interface EnhancedTaskCardProps {
  task: EnhancedTask;
  onEdit: (task: EnhancedTask) => void;
  onDelete: (id: string) => void;
  onView: (task: EnhancedTask) => void;
  onToggleStatus: (task: EnhancedTask) => void;
  onStartTimer?: (taskId: string) => void;
  onStopTimer?: (taskId: string) => void;
  isTimerRunning?: boolean;
  /** Início do cronômetro (ms), quando estiver rodando */
  timerStartedAt?: number;
}

export const EnhancedTaskCard = ({
  task,
  onEdit,
  onDelete,
  onView,
  onToggleStatus,
  onStartTimer,
  onStopTimer,
  isTimerRunning = false,
  timerStartedAt
}: EnhancedTaskCardProps) => {
  const [showDetails, setShowDetails] = useState(false);
  const [now, setNow] = useState(Date.now());

  // Atualiza a contagem a cada segundo enquanto o cronômetro roda
  useEffect(() => {
    if (!isTimerRunning) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isTimerRunning]);

  const elapsedSeconds = isTimerRunning && timerStartedAt ? Math.max(0, Math.floor((now - timerStartedAt) / 1000)) : 0;
  const formatClock = (total: number) =>
    [Math.floor(total / 3600), Math.floor((total % 3600) / 60), total % 60].map((n) => String(n).padStart(2, '0')).join(':');
  const workedSeconds = getTrackedSeconds(task);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "Alta": return "bg-destructive text-destructive-foreground";
      case "Média": return "bg-warning text-warning-foreground";
      case "Baixa": return "bg-secondary text-secondary-foreground";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Concluída": return "bg-tertiary text-tertiary-foreground";
      case "Em andamento": return "bg-primary text-primary-foreground";
      case "Pendente": return "bg-warning text-warning-foreground";
      case "Cancelada": return "bg-muted text-muted-foreground";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const getTaskTypeIcon = (type?: string) => {
    switch (type) {
      case "prazo": return <AlertTriangle className="w-4 h-4" />;
      case "reuniao": return <Users className="w-4 h-4" />;
      case "pesquisa": return <FileText className="w-4 h-4" />;
      case "audiencia": return <Calendar className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const isOverdue = (dueDate: string) => {
    if (!dueDate) return false;
    
    try {
      const due = new Date(dueDate);
      const today = new Date();
      
      // Comparar apenas as datas (ano, mês, dia)
      const dueDateOnly = new Date(due.getFullYear(), due.getMonth(), due.getDate());
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      
      return dueDateOnly < todayOnly;
    } catch (error) {
      console.error('Erro ao verificar se está atrasada:', error);
      return false;
    }
  };

  // Cor do filete automática: atrasada (vermelho), prioridade alta (laranja), concluída (verde) ou dourado da marca
  const accentColor =
    task.status === 'Concluída' ? '#10b981' :
    isOverdue(task.due_date || '') ? '#ef4444' :
    task.priority === 'Alta' ? '#f97316' :
    '#c4ab83';

  const formatCurrency = (value?: number) => {
    if (!value) return "R$ 0,00";
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const calculateBillableAmount = () => {
    if (!task.is_billable || !task.actual_hours || !task.hourly_rate) return 0;
    return task.actual_hours * task.hourly_rate;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    
    try {
      const date = new Date(dateString);
      // Usar fuso horário de Brasília
      return date.toLocaleDateString('pt-BR', {
        timeZone: 'America/Sao_Paulo'
      });
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return dateString;
    }
  };

  return (
    <TooltipProvider>
      <Card className="group hover:shadow-lg transition-all duration-200 border-l-4" 
            style={{ borderLeftColor: accentColor }}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {getTaskTypeIcon(task.task_type)}
              <div className="flex-1 min-w-0">
                <CardTitle className="text-sm font-medium truncate">
                  {task.title}
                </CardTitle>
                {task.cases?.title && (
                  <p className="text-xs text-muted-foreground truncate">
                    {task.cases.title}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {task.milestone && (
                <Tooltip>
                  <TooltipTrigger>
                    <Badge variant="outline" className="text-xs">
                      <Star className="w-3 h-3 mr-1" />
                      Marco
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>Marco importante</TooltipContent>
                </Tooltip>
              )}
              {task.deadline_critical && (
                <Tooltip>
                  <TooltipTrigger>
                    <Badge variant="destructive" className="text-xs">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Crítico
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>Prazo crítico</TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          {/* Description */}
          {task.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {task.description}
            </p>
          )}

          {/* Status, Prioridade e botão de conclusão bem evidente */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Badge className={getStatusColor(task.status)}>
                {task.status}
              </Badge>
              <Badge className={getPriorityColor(task.priority)}>
                {task.priority}
              </Badge>
              {isOverdue(task.due_date || "") && task.status !== "Concluída" && (
                <Badge variant="destructive" className="text-xs">
                  Atrasada
                </Badge>
              )}
              {(task as any).show_in_portal && (
                (task as any).portal_published_at ? (
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700" title="Publicado no Portal do Cliente">
                    No portal ✓
                  </span>
                ) : (task as any).portal_error ? (
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700" title={(task as any).portal_error}>
                    Portal: não publicado
                  </span>
                ) : (
                  <span className="rounded-full bg-[#c4ab83]/15 px-2 py-0.5 text-[11px] font-medium text-[#7a6440]" title="Ao concluir, aparece no Portal do Cliente">
                    Vai para o portal
                  </span>
                )
              )}
            </div>

            {task.status === "Concluída" ? (
              // Concluída: mostra o estado, e "reabrir" fica discreto
              <div className="flex w-full items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
                  <CheckCircle className="h-4 w-4" /> Concluída
                </span>
                <button
                  type="button"
                  onClick={() => onToggleStatus(task)}
                  className="rounded-md px-2 py-0.5 text-xs text-emerald-700/70 transition-colors hover:bg-white hover:text-emerald-800"
                  title="Voltar a tarefa para pendente"
                >
                  Reabrir
                </button>
              </div>
            ) : (
              <Button
                type="button"
                size="sm"
                className="w-full justify-center gap-2 bg-emerald-600 text-white hover:bg-emerald-700"
                onClick={() => onToggleStatus(task)}
              >
                <CheckCircle className="w-4 h-4" />
                Marcar como concluída
              </Button>
            )}
          </div>


          {/* Checklist */}
          {Array.isArray(task.checklist) && task.checklist.length > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <CheckCircle className="h-3.5 w-3.5" />
              Checklist {task.checklist.filter((i: any) => i.completed).length}/{task.checklist.length}
            </div>
          )}

          {/* Cronômetro e tempo trabalhado */}
          {(isTimerRunning || workedSeconds > 0) && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {isTimerRunning && (
                <button
                  type="button"
                  onClick={() => onStopTimer?.(task.id)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#c4ab83]/15 px-2.5 py-1 font-mono font-semibold text-[#7a6440] transition-colors hover:bg-[#c4ab83]/25"
                  title="Parar e registrar o tempo"
                >
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#9a7f52]" />
                  {formatClock(elapsedSeconds)}
                  <Pause className="h-3 w-3" />
                </button>
              )}
              {workedSeconds > 0 && (
                <span className="inline-flex items-center gap-1 text-slate-500">
                  <Timer className="h-3.5 w-3.5" />
                  {formatDuration(workedSeconds)} trabalhados
                </span>
              )}
            </div>
          )}

          {/* Due Date */}
          {task.due_date && (
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className={isOverdue(task.due_date) && task.status !== "Concluída" ? "text-destructive font-medium" : ""}>
                {formatDate(task.due_date)}
                {isOverdue(task.due_date) && task.status !== "Concluída" && " (Atrasada)"}
              </span>
            </div>
          )}

          {/* Assigned To */}
          {task.assigned_to && (
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-muted-foreground" />
              <span>{task.assigned_to}</span>
            </div>
          )}

          {/* Billable Info */}
          {task.is_billable && (
            <div className="flex items-center gap-2 text-sm">
              <DollarSign className="w-4 h-4 text-muted-foreground" />
              <span>{formatCurrency(calculateBillableAmount())}</span>
              {task.actual_hours && (
                <span className="text-muted-foreground">
                  ({task.actual_hours}h)
                </span>
              )}
            </div>
          )}

          {/* Tags */}
          {task.tags && task.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {task.tags.slice(0, 3).map((tag, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
              {task.tags.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{task.tags.length - 3}
                </Badge>
              )}
            </div>
          )}

          {/* Action Buttons (sem o toggle de status, que agora é o botão grande acima) */}
          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onView(task)}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Ver detalhes</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(task)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Editar</TooltipContent>
              </Tooltip>

              {onStartTimer && onStopTimer && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => isTimerRunning ? onStopTimer(task.id) : onStartTimer(task.id)}
                      className={isTimerRunning ? "bg-[#c4ab83]/15 text-[#7a6440] hover:bg-[#c4ab83]/25" : ""}
                    >
                      {isTimerRunning ? (
                        <Pause className="w-4 h-4" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isTimerRunning ? "Parar e registrar o tempo" : "Iniciar cronômetro"}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(task.id)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Excluir</TooltipContent>
            </Tooltip>
    </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};